import React, { useState, useEffect } from 'react';
import { apiClient, Folder } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './ReviewModal.css';

interface ReviewModalProps {
  folder: Folder;
  allUsers: { email: string; name: string }[];
  mode: 'submit' | 'review' | 'view';
  onClose: () => void;
  onDone: () => void;
}

const CHECKLIST_ITEMS = [
  'Verified functional scenarios',
  'Verified edge case scenarios',
  'Verified cross app impact scenarios',
];

export const ReviewModal: React.FC<ReviewModalProps> = ({ folder, allUsers: _allUsers, mode, onClose, onDone }) => {
  const { user } = useAuth();
  const [reviewerEmail, setReviewerEmail] = useState('');
  const [reviewerSearch, setReviewerSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [users, setUsers] = useState<{ email: string; name: string; role: string }[]>([]);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState<any[]>([]);
  const [checklist, setChecklist] = useState<boolean[]>(CHECKLIST_ITEMS.map(() => false));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient.getFolderComments(folder.id).then(setComments).catch(() => {});
    if (mode === 'submit') {
      apiClient.getUsersForReview().then(setUsers).catch(() => {});
    }
  }, [folder.id, mode]);

  const filteredUsers = users.filter(u =>
    u.email !== user?.email &&
    (u.name.toLowerCase().includes(reviewerSearch.toLowerCase()) ||
     u.email.toLowerCase().includes(reviewerSearch.toLowerCase()))
  );

  const toggleCheck = (i: number) => {
    setChecklist(prev => prev.map((v, idx) => idx === i ? !v : v));
  };

  const handleSelectReviewer = (u: { email: string; name: string }) => {
    setReviewerEmail(u.email);
    setReviewerSearch(u.name);
    setShowDropdown(false);
  };

  const handleSubmit = async () => {
    if (!reviewerEmail.trim()) { setError('Please select a reviewer'); return; }
    setIsLoading(true);
    try {
      await apiClient.submitForReview(folder.id, reviewerEmail.trim(), comment.trim() || undefined);
      onDone();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to submit for review');
    } finally { setIsLoading(false); }
  };

  const allChecked = checklist.every(v => v);

  const handleApprove = async () => {
    if (!allChecked) { setError('Please complete all verification checklist items before approving'); return; }
    setIsLoading(true);
    try {
      await apiClient.reviewAction(folder.id, 'approve', comment || undefined);
      onDone();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed');
    } finally { setIsLoading(false); }
  };

  const handleRequestChanges = async () => {
    if (!comment.trim()) { setError('A comment is required when requesting changes'); return; }
    setIsLoading(true);
    try {
      await apiClient.reviewAction(folder.id, 'request_changes', comment);
      onDone();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed');
    } finally { setIsLoading(false); }
  };

  const title = mode === 'submit'
    ? `Submit "${folder.name}" for Review`
    : mode === 'view'
    ? `Review Details: ${folder.name}`
    : `Review: ${folder.name}`;

  return (
    <div className="rm-overlay" onClick={onClose}>
      <div className="rm-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="rm-header">
          <h2 className="rm-title">{title}</h2>
          <button className="rm-close" onClick={onClose}>×</button>
        </div>

        {/* Body */}
        <div className="rm-body">
          {error && <div className="rm-error">{error}</div>}

          {mode === 'review' && (
            <div className="rm-meta-row">
              <span className="rm-meta-label">Submitted by</span>
              <span className="rm-meta-value">{folder.submitted_by || '—'}</span>
              {folder.submitted_at && (
                <span className="rm-meta-date">{new Date(folder.submitted_at).toLocaleString()}</span>
              )}
            </div>
          )}

          {/* View mode — show full review summary, read-only */}
          {mode === 'view' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div className="rm-meta-row">
                <span className="rm-meta-label">Submitted by</span>
                <span className="rm-meta-value">{folder.submitted_by || '—'}</span>
                {folder.submitted_at && <span className="rm-meta-date">{new Date(folder.submitted_at).toLocaleString()}</span>}
              </div>
              <div className="rm-meta-row">
                <span className="rm-meta-label">Assigned Reviewer</span>
                <span className="rm-meta-value">{folder.reviewer_email || '—'}</span>
              </div>
              {folder.review_status === 'Approved' && (
                <div className="rm-meta-row">
                  <span className="rm-meta-label">Reviewed on</span>
                  <span className="rm-meta-value">{folder.reviewed_at ? new Date(folder.reviewed_at).toLocaleString() : '—'}</span>
                </div>
              )}
              <div className="rm-meta-row">
                <span className="rm-meta-label">Status</span>
                <span className="rm-meta-value" style={{
                  color: folder.review_status === 'Approved' ? '#2ecc71' : '#29b6f6',
                  fontWeight: 700
                }}>
                  {folder.review_status === 'Approved' ? '✅' : '🔍'} {folder.review_status}
                </span>
              </div>
            </div>
          )}

          {/* Submit mode — reviewer searchable dropdown */}
          {mode === 'submit' && (
            <div className="rm-field" style={{ position: 'relative' }}>
              <label className="rm-label">Assign Reviewer <span className="rm-required">*</span></label>
              <input
                type="text"
                className="rm-input"
                placeholder="Search by name or email..."
                value={reviewerSearch}
                onChange={e => {
                  setReviewerSearch(e.target.value);
                  setReviewerEmail('');
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                autoComplete="off"
              />
              {showDropdown && filteredUsers.length > 0 && (
                <div className="rm-user-dropdown">
                  {filteredUsers.map(u => (
                    <div
                      key={u.email}
                      className="rm-user-option"
                      onMouseDown={() => handleSelectReviewer(u)}
                    >
                      <div className="rm-user-name">{u.name}</div>
                      <div className="rm-user-email">{u.email} <span className="rm-user-role">{u.role}</span></div>
                    </div>
                  ))}
                </div>
              )}
              {reviewerEmail && (
                <div className="rm-selected-reviewer">
                  ✅ {reviewerEmail}
                </div>
              )}
            </div>
          )}

          {/* Verification checklist — review mode only */}
          {mode === 'review' && (
            <div className="rm-section">
              <div className="rm-section-label">VERIFICATION CHECKLIST <span className="rm-required">*</span></div>
              {CHECKLIST_ITEMS.map((item, i) => (
                <label key={i} className="rm-check-row">
                  <input type="checkbox" className="rm-checkbox" checked={checklist[i]} onChange={() => toggleCheck(i)} />
                  <span>{item}</span>
                </label>
              ))}
            </div>
          )}

          {/* Previous comments */}
          {comments.length > 0 && (
            <div className="rm-section">
              <div className="rm-section-label">PREVIOUS COMMENTS</div>
              <div className="rm-comments">
                {comments.map((c, i) => (
                  <div key={i} className="rm-comment-item">
                    <div className="rm-comment-meta">{c.author_email} · {new Date(c.created_at).toLocaleString()}</div>
                    <div className="rm-comment-text" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.comment}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comment box */}
          <div className="rm-section">
            <div className="rm-section-label">
              COMMENT
              {mode === 'review' && <span className="rm-required-note"> (required for Request Changes)</span>}
              {mode === 'submit' && <span className="rm-required-note" style={{ color: '#6b7280' }}> (optional — visible to reviewer)</span>}
            </div>
            <textarea
              className="rm-textarea"
              rows={4}
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Provide detailed feedback for the author..."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="rm-footer">
          <button className="rm-btn rm-btn-cancel" onClick={onClose}>
            {mode === 'view' ? 'Close' : 'Cancel'}
          </button>
          {mode === 'submit' && (
            <button className="rm-btn rm-btn-primary" onClick={handleSubmit} disabled={isLoading || !reviewerEmail}>
              {isLoading ? 'Submitting...' : '📋 Submit for Review'}
            </button>
          )}
          {mode === 'review' && (
            <>
              <button className="rm-btn rm-btn-changes" onClick={handleRequestChanges} disabled={isLoading}>
                ↩ Request Changes
              </button>
              <button className="rm-btn rm-btn-approve" onClick={handleApprove} disabled={isLoading || !allChecked} title={!allChecked ? 'Complete all checklist items to approve' : ''}>
                ✔ Approve
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
